import { differenceWith, isEqual } from 'lodash';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Plan,
  PlanLicense,
  Price,
  useGetPlanDetailsQuery,
  useListPlansQuery,
} from '../../api/graphql/generated';
import { usePrevious } from '../../util/hooks';
import { SubscriptionBillingInterval } from './subscription.types';

export interface SubscriptionCartItem {
  price: Price;
  plan: Plan;
  quantity: number;
  assignedToIds?: string[];
}

export interface SubscriptionCart {
  total: number;
  currency: string;
  interval: SubscriptionBillingInterval;
  items: SubscriptionCartItem[];
}

export interface SubscriptionContextValue {
  plans: Plan[];
  existingLicenses: PlanLicense[];
  hasExistingSubscription: boolean;
  hasPendingChangesInCart: boolean;
  cart: SubscriptionCart;
  addToCart: (item: SubscriptionCartItem) => void;
  removeFromCart: (priceId: string) => void;
  setCartBillingInterval: (interval: SubscriptionBillingInterval) => void;
}

export const SubscriptionContext = createContext<
  SubscriptionContextValue | undefined
>(undefined);

export interface SubscriptionContextProviderProps {
  children: ReactNode;
}

const SubscriptionContextProvider = (
  props: SubscriptionContextProviderProps
) => {
  const { children } = props;

  const [cart, setCart] = useState<SubscriptionCart>({
    total: 0,
    items: [],
    currency: 'usd',
    interval: SubscriptionBillingInterval.YEAR,
  });

  const resetCart = useCallback(() => {
    const emptyCart: SubscriptionCart = {
      total: 0,
      items: [],
      currency: 'usd',
      interval: SubscriptionBillingInterval.YEAR,
    };
    setCart(emptyCart);
    cartRef.current = emptyCart;
  }, []);

  const cartRef = useRef<SubscriptionCart>(cart);
  cartRef.current = cart; // sync ref with state on every render

  const { data: plansData } = useListPlansQuery({
    variables: {
      input: {
        trialAvailable: undefined,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
  });

  const { data: planDetails } = useGetPlanDetailsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
  });

  const prevPlanDetails = usePrevious(planDetails);

  const getPlanById = useCallback(
    (options: { planId?: string | null }) => {
      const { planId } = options;
      const plans = plansData?.listPlans?.items ?? [];
      return plans.find((p) => p.id === planId);
    },
    [plansData?.listPlans.items]
  );

  const getPriceById = useCallback(
    (options: { planId?: string | null; priceId?: string | null }) => {
      const { planId, priceId } = options;
      const plan = getPlanById({ planId });
      const prices = plan?.product?.prices ?? [];
      return prices.find((p) => p.id === priceId);
    },
    [getPlanById]
  );

  const buildSubscriptionCartItemFromLicense = useCallback(
    (license: PlanLicense): SubscriptionCartItem | undefined => {
      const plan = getPlanById({
        planId: license.planId,
      });

      const price = getPriceById({
        planId: license.planId,
        priceId: license.priceId,
      });

      if (plan && price) {
        return {
          plan,
          price,
          quantity: license.quantity,
          assignedToIds: license.assignments?.map((a) => a.assignedTo),
        };
      }
    },
    [getPlanById, getPriceById]
  );

  const existingLicenses = useMemo(
    () => planDetails?.getPlanDetails?.licenses ?? [],
    [planDetails?.getPlanDetails?.licenses]
  );

  const existingCartItems = useMemo(() => {
    const items = new Array<SubscriptionCartItem>();
    for (const license of existingLicenses) {
      const item = buildSubscriptionCartItemFromLicense(license as PlanLicense);
      if (item) {
        items.push(item);
      }
    }

    return items;
  }, [existingLicenses, buildSubscriptionCartItemFromLicense]);

  const [
    hydratedCartWithExistingLicenses,
    setHydratedCartWithExistingLicenses,
  ] = useState(false);

  useEffect(() => {
    // When the plan details change, such as after the user has
    // updated or cancelled their subscription, we need to recalculate
    // the cart to reflect the changes in the UI.
    if (planDetails && prevPlanDetails && planDetails !== prevPlanDetails) {
      setHydratedCartWithExistingLicenses(false);
    }
  }, [planDetails, prevPlanDetails]);

  const addToCart = useCallback((item: SubscriptionCartItem) => {
    // Remove the item from our cached list.
    // If the item has a quantity then we'll add it back with the new value.
    // Otherwise, the zero quantity is treated as a removal.
    const updatedItems = cartRef.current.items.filter(
      (i) => i.price.id !== item.price.id
    );

    // only add items with quantity > 0
    if (item.quantity > 0) {
      updatedItems.push(item);
    }

    // proactively update the cart ref items since state propagations are async
    cartRef.current.items = updatedItems;

    // ensure the cart reflects the billing interval of the item
    cartRef.current.interval = item.price.recurringInterval;

    setCart({
      ...cartRef.current,
      total: calculateCartTotal(updatedItems),
      items: updatedItems,
    });
  }, []);

  const removeFromCart = useCallback((priceId: string) => {
    // Remove the item from our cached list.
    const updatedItems = cartRef.current.items.filter(
      (i) => i.price.id !== priceId
    );

    // proactively update the cart ref items since state propagations are async
    cartRef.current.items = updatedItems;

    setCart({
      ...cartRef.current,
      total: calculateCartTotal(updatedItems),
      items: updatedItems,
    });
  }, []);

  useEffect(() => {
    if (hydratedCartWithExistingLicenses) {
      return;
    }

    // Ensure cart is empty before we hydrate with existing licenses.
    // Otherwise stale data from the previous subscription may be present.
    resetCart();

    existingCartItems.forEach((item) => {
      addToCart(item);
    });

    setHydratedCartWithExistingLicenses(true);
  }, [
    hydratedCartWithExistingLicenses,
    existingCartItems,
    resetCart,
    addToCart,
  ]);

  const setCartBillingInterval = useCallback(
    (interval: SubscriptionBillingInterval) => {
      setCart({
        ...cartRef.current,
        interval,
      });
    },
    []
  );

  const contextValue = useMemo<SubscriptionContextValue>(() => {
    const plans = [...(plansData?.listPlans?.items ?? [])];

    plans.sort((p1: Plan, p2: Plan): number => {
      return p1.displayOrder - p2.displayOrder;
    });

    const nonDefaultPlans = plans.filter((p) => !p.default);

    let hasExistingSubscription = false;

    for (const existingLicense of existingLicenses) {
      if (nonDefaultPlans.some((p) => p.id === existingLicense.planId)) {
        hasExistingSubscription = true;
        break;
      }
    }

    const hasPendingChangesInCart =
      cart.items.length !== existingCartItems.length ||
      differenceWith(cart.items, existingCartItems, (a, b) => {
        return (
          a.plan.id === b.plan.id &&
          a.price.id === b.price.id &&
          a.quantity === b.quantity &&
          isEqual(a.assignedToIds ?? [], b.assignedToIds ?? [])
        );
      }).length > 0;

    return {
      plans: plans as Plan[],
      existingLicenses: existingLicenses as PlanLicense[],
      hasExistingSubscription,
      hasPendingChangesInCart,
      cart,
      addToCart,
      removeFromCart,
      setCartBillingInterval,
    };
  }, [
    existingLicenses,
    existingCartItems,
    plansData,
    cart,
    addToCart,
    removeFromCart,
    setCartBillingInterval,
  ]);

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

const useSubscriptionContext = () => {
  return useContext(SubscriptionContext) as SubscriptionContextValue;
};

const calculateCartTotal = (items: SubscriptionCartItem[]) => {
  let total = 0;

  for (const item of items) {
    total += item.price.amount * item.quantity;
  }

  return total;
};

export { SubscriptionContextProvider, useSubscriptionContext };
